import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SecretInput from '@/components/SecretInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, Loader2, Database, RefreshCw, Mail, Send, CheckCircle2, Trash2, AlertTriangle, ShieldAlert, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/dateFormat';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

const SECURITY_PORT = { tls: 465, starttls: 587, none: 25 };
const isLocalHost = (h) => /^(localhost|127\.0\.0\.1|::1|\[::1\])$/i.test((h || '').trim());

function BriefingSettings({ api }) {
  const [threshold, setThreshold] = useState(50);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/briefing/settings').then(r => { setThreshold(r.data.threshold || 50); setLoaded(true); }).catch(() => setLoaded(true));
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/briefing/settings', { threshold });
      toast.success('Briefing settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div className="bg-card border border-border rounded-md p-5">
      <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2">
        <Settings size={16} strokeWidth={1.5} /> Beat Briefing Settings
      </h3>
      <p className="text-xs text-muted-foreground mb-4">Configure how beat briefing recommendations are generated.</p>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Category Push Threshold</Label>
          <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">If a category is ordered by this % of outlets in a beat, it will be recommended to outlets that don't order it.</p>
          <div className="flex items-center gap-3">
            <Input type="number" min={10} max={90} value={threshold} onChange={e => setThreshold(parseInt(e.target.value) || 50)} className="w-20 h-8" data-testid="briefing-threshold" />
            <span className="text-xs text-muted-foreground">%</span>
            <input type="range" min={10} max={90} value={threshold} onChange={e => setThreshold(parseInt(e.target.value))} className="flex-1 h-1 accent-primary" />
            <Button size="sm" onClick={save} disabled={saving} className="h-8 text-xs">
              {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : null} Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildMonthRange(dataMonths, maxMonths = 12) {
  if (!dataMonths || dataMonths.length === 0) return [];
  const byMonth = {};
  dataMonths.forEach(m => { byMonth[m.month] = m; });
  const months = Object.keys(byMonth).sort();
  const first = months[0];
  const last = months[months.length - 1];
  // Generate all months between first and last
  const all = [];
  let [y, m] = first.split('-').map(Number);
  const [ly, lm] = last.split('-').map(Number);
  while (y < ly || (y === ly && m <= lm)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    all.push({ month: key, ...(byMonth[key] || { records: 0, missing: true }) });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  // Show last maxMonths
  return all.length > maxMonths ? all.slice(-maxMonths) : all;
}

function CoverageBar({ data, label, color = 'bg-primary', maxMonths = 12 }) {
  const months = buildMonthRange(data, maxMonths);
  if (months.length === 0) return null;
  const filled = months.filter(m => !m.missing);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium">{label}</span>
        <span className="text-[10px] text-muted-foreground">{filled[0]?.month} to {filled[filled.length - 1]?.month} ({filled.length} months{months.length > filled.length ? `, ${months.length - filled.length} gap${months.length - filled.length > 1 ? 's' : ''}` : ''})</span>
      </div>
      <div className="flex gap-1">
        {months.map(m => (
          <div key={m.month} className="flex-1"
            title={m.missing ? `${m.month}: No data uploaded` : `${m.month}: ${m.records?.toLocaleString('en-IN')} records${m.outlets ? `, ${m.outlets.toLocaleString('en-IN')} outlets` : ''}${m.skus ? `, ${m.skus} SKUs` : ''}`}>
            {m.missing ? (
              <div className="h-8 rounded-sm border-2 border-dashed border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20 flex items-center justify-center">
                <span className="text-[8px] text-red-400 font-medium">GAP</span>
              </div>
            ) : (
              <div className={`h-8 ${color} rounded-sm flex items-end justify-center overflow-hidden`}>
                <span className="text-[8px] text-white/80 pb-0.5 font-medium">{m.records > 999 ? `${(m.records / 1000).toFixed(0)}K` : m.records}</span>
              </div>
            )}
            <p className="text-[9px] text-muted-foreground text-center mt-0.5">{m.month.slice(2)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


const FILE_TYPES = [
  { value: 'sales', label: 'Sales Data (Primary Report)', desc: 'West1 primary report CSV' },
  { value: 'order_fulfilment', label: 'Order vs Fulfilment', desc: 'Order fulfilment report CSV' },
  { value: 'outlets', label: 'Outlet Master', desc: 'Outlet/shop master data CSV' },
  { value: 'skus', label: 'SKU Master', desc: 'Product/SKU master CSV' },
  { value: 'users', label: 'Field Executives', desc: 'Sales team / field executive list CSV' },
  { value: 'beat_plans', label: 'Beat Plan', desc: 'Beat plan/route CSV. Uploading it rebuilds tomorrow’s briefings from what it says' },
  { value: 'fe_visits_pc', label: 'FE Visits - Orders (PC)', desc: 'Productive calls — visits with orders, SKU-level detail' },
  { value: 'fe_visits_tc', label: 'FE Visits - All Calls (TC)', desc: 'Total calls — all visits including non-converting ones' },
  { value: 'fe_targets', label: 'FE Monthly Targets', desc: 'Field executive monthly sales targets (User ID, User Name, Month, Target). Month e.g. Sep-2026; without it, the current month' },
  { value: 'beat_jumps', label: 'Beat Jumps', desc: 'The morning’s approved beat jumps. The day it covers replaces whatever was uploaded for that day before, and the jumped beat takes the place of the FE’s planned beat in that evening’s reports' },
];

export default function ConfigurationPage() {
  const { api, user, isSuper } = useAuth();
  const [fileType, setFileType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [history, setHistory] = useState([]);
  const [dataStatus, setDataStatus] = useState({});
  const fileRef = useRef(null);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState('');
  const [uploadEta, setUploadEta] = useState('');
  const uploadStartRef = useRef(null);

  // SMTP
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', security: 'tls' });
  const [smtpHasPassword, setSmtpHasPassword] = useState(false);   // saved on the server; the field stays blank
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');

  // Data Reset
  const [resetStep, setResetStep] = useState(0);
  const [resetOtp, setResetOtp] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  // Claude API Key
  const [aiKey, setAiKey] = useState('');
  const [aiKeyInfo, setAiKeyInfo] = useState(null);
  const [aiKeySaving, setAiKeySaving] = useState(false);

  // Duplicate detection
  const [dupInfo, setDupInfo] = useState(null);
  const [dupChecking, setDupChecking] = useState(false);
  const [forceUpload, setForceUpload] = useState(false);
  const [replacePrevious, setReplacePrevious] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildAsk, setRebuildAsk] = useState(false);

  const loadAiKey = async () => {
    try { const r = await api.get('/config/ai-key'); setAiKeyInfo(r.data); } catch {}
  };

  const loadSmtp = async () => {
    try {
      const res = await api.get('/config/smtp');
      if (res.data.configured) {
        setSmtp({ host: res.data.host || '', port: res.data.port || 587, username: res.data.username || '', password: '', from_email: res.data.from_email || '',
          // the server works out the mode for settings saved before it was a field of its own
          security: res.data.security || (res.data.use_tls !== false ? 'tls' : 'starttls') });
        setSmtpHasPassword(!!res.data.password);
      }
    } catch (e) { console.error(e); }
    finally { setSmtpLoading(false); }
  };

  const loadCoverage = () => api.get('/upload/coverage').then(r => setCoverage(r.data)).catch(e => console.error('coverage error:', e));

  const loadHistory = async () => {
    try {
      const histRes = await api.get('/upload/history');
      setHistory(histRes.data);
    } catch (e) { console.error(e); }
    try {
      const statusRes = await api.get('/upload/status');
      setDataStatus(statusRes.data);
    } catch (e) { console.error(e); }
    loadCoverage();
  };

  useEffect(() => { loadHistory(); if (isSuper) { loadSmtp(); loadAiKey(); } }, [api, isSuper]);

  // Rebuilding the summary tables truncates and refills them. It is the right fix when the
  // numbers look stale, and the wrong thing to fire in the evening: the 6:15 and 8:30 runs
  // read those tables and will sit behind the lock until this finishes.
  const doRebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.post('/upload/rebuild-summaries');
      toast.success(r.data?.message || 'Summaries rebuilt');
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rebuild failed'); }
    finally { setRebuilding(false); setRebuildAsk(false); }
  };

  const handleUpload = async (force = false) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    if (!fileType) { toast.error('Please select a file type first'); return; }
    const validExts = ['.csv', '.gz', '.zip'];
    const hasValidExt = validExts.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!hasValidExt) { toast.error('Supported formats: CSV, GZ, ZIP'); return; }

    // Check for duplicate first (lightweight — just filename + size)
    if (!force) {
      setDupChecking(true);
      try {
        const checkRes = await api.post('/upload/check-duplicate', { file_name: file.name, file_size: file.size });
        if (checkRes.data.duplicate) {
          setDupInfo(checkRes.data);
          setDupChecking(false);
          return;
        }
      } catch {}
      setDupChecking(false);
    }
    setDupInfo(null);
    setForceUpload(false);

    setUploading(true);
    setUploadProgress(0);
    uploadStartRef.current = Date.now();

    // Determine if we should compress client-side
    const isRawCsv = file.name.toLowerCase().endsWith('.csv');
    const isLargeEnough = file.size > 500 * 1024; // Only compress files > 500KB
    const canCompress = isRawCsv && isLargeEnough && typeof CompressionStream !== 'undefined';

    let uploadFile = file;

    if (canCompress) {
      setUploadPhase('compressing');
      setUploadEta('Compressing...');
      try {
        const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
        const compressedBlob = await new Response(stream).blob();
        const ratio = Math.round((1 - compressedBlob.size / file.size) * 100);
        uploadFile = new File([compressedBlob], file.name + '.gz', { type: 'application/gzip' });
        toast.info(`Compressed ${ratio}% (${(file.size / 1048576).toFixed(1)}MB \u2192 ${(compressedBlob.size / 1048576).toFixed(1)}MB)`);
      } catch (e) {
        console.warn('Compression failed, uploading raw:', e);
        uploadFile = file;
      }
    }

    setUploadPhase('uploading');
    setUploadEta('Calculating...');

    const fileSizeMB = uploadFile.size / (1024 * 1024);
    const estimatedProcessSec = Math.max(3, Math.round(fileSizeMB * 2));

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      // Send original filename so upload_history stores the pre-compression name (for duplicate detection)
      formData.append('original_filename', file.name);

      const urlParams = new URLSearchParams();
      if (force) urlParams.set('force', 'true');
      if (replacePrevious && (fileType === 'beat_plans' || fileType === 'outlets')) urlParams.set('replace', 'true');
      const uploadUrl = `/upload/${fileType}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      const res = await api.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const pct = progressEvent.total ? Math.round((progressEvent.loaded / progressEvent.total) * 50) : 0;
          setUploadProgress(pct);
          const elapsed = (Date.now() - uploadStartRef.current) / 1000;
          if (pct > 5 && elapsed > 0) {
            const totalUploadTime = (elapsed / pct) * 50;
            const remainingUpload = totalUploadTime - elapsed;
            const totalEta = Math.round(remainingUpload + estimatedProcessSec);
            setUploadEta(totalEta > 60 ? `~${Math.round(totalEta / 60)}m ${totalEta % 60}s` : `~${totalEta}s`);
          }
        },
      });

      // Handle async (large file) vs sync (small file) response
      if (res.data.async) {
        // Large file — poll for job completion
        setUploadPhase('processing');
        setUploadProgress(55);
        setUploadEta('Processing large file...');
        const jobId = res.data.job_id;
        const pollInterval = setInterval(async () => {
          try {
            const jobRes = await api.get(`/upload/job/${jobId}`);
            const job = jobRes.data;
            if (job.status === 'complete') {
              clearInterval(pollInterval);
              setUploadProgress(100);
              setUploadPhase('done');
              setUploadEta('');
              toast.success(`${job.cleared ? `Cleared ${job.cleared.toLocaleString('en-IN')} previous records. ` : ''}Processed ${job.count} records (${job.inserted || 0} new, ${job.updated || 0} updated, ${job.total_in_db || 0} total in DB)${job.beats_resolved ? `. Kept one beat for ${job.beats_resolved.toLocaleString('en-IN')} outlets that listed several` : ''}${job.retired ? `. Marked ${job.retired.toLocaleString('en-IN')} outlets not in this file as inactive` : ''}`);
              setReplacePrevious(false);
              fileRef.current.value = '';
              loadHistory();
              setTimeout(() => { setUploadProgress(0); setUploadPhase(''); }, 2000);
              setUploading(false);
            } else if (job.status === 'error') {
              clearInterval(pollInterval);
              toast.error(job.message || 'Processing failed');
              setUploadProgress(0); setUploadPhase(''); setUploadEta('');
              setUploading(false);
            } else {
              // Still processing — increment progress slowly
              setUploadProgress(p => Math.min(p + 1, 95));
              setUploadEta(job.progress || 'Processing...');
            }
          } catch { /* keep polling */ }
        }, 3000);
      } else {
        // Small file — already done
        setUploadProgress(100);
        setUploadPhase('done');
        setUploadEta('');
        toast.success(`${res.data.cleared ? `Cleared ${res.data.cleared.toLocaleString('en-IN')} previous records. ` : ''}Processed ${res.data.count} records (${res.data.inserted || 0} new, ${res.data.updated || 0} updated, ${res.data.total_in_db || 0} total in DB)${res.data.beats_resolved ? `. Kept one beat for ${res.data.beats_resolved.toLocaleString('en-IN')} outlets that listed several` : ''}${res.data.retired ? `. Marked ${res.data.retired.toLocaleString('en-IN')} outlets not in this file as inactive` : ''}`);
        setReplacePrevious(false);
        fileRef.current.value = '';
        loadHistory();
        setTimeout(() => { setUploadProgress(0); setUploadPhase(''); }, 2000);
        setUploading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setUploadProgress(0);
      setUploadPhase('');
      setUploadEta('');
      setUploading(false);
    }
  };

  // Simulate processing progress after upload reaches 50%
  useEffect(() => {
    if (uploadPhase === 'uploading' && uploadProgress >= 50 && uploading) {
      setUploadPhase('processing');
      const fileSizeMB = fileRef.current?.files?.[0]?.size / (1024 * 1024) || 1;
      const estimatedMs = Math.max(3000, fileSizeMB * 2000);
      const interval = 200;
      const steps = estimatedMs / interval;
      let step = 0;
      const timer = setInterval(() => {
        step++;
        const processPct = Math.min(49, (step / steps) * 49);
        setUploadProgress(50 + Math.round(processPct));
        const remainSec = Math.max(1, Math.round(((steps - step) * interval) / 1000));
        setUploadEta(remainSec > 60 ? `~${Math.round(remainSec / 60)}m ${remainSec % 60}s` : `~${remainSec}s`);
        if (step >= steps || !uploading) { clearInterval(timer); }
      }, interval);
      return () => clearInterval(timer);
    }
  }, [uploadPhase, uploadProgress, uploading]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/upload/seed');
      toast.success('Data seeded successfully');
      loadHistory();
    } catch (err) {
      toast.error('Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="configuration-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">{isSuper ? 'Upload data and manage settings' : 'Upload the daily data files the reports are built from'}</p>
      </div>

      {/* Data Status */}
      <div className="bg-card border border-border rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
            <Database size={16} strokeWidth={1.5} /> Data Status
          </h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setRebuildAsk(true)} disabled={rebuilding} data-testid="rebuild-summaries-btn">
              {rebuilding ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Database size={14} className="mr-1" />}
              {rebuilding ? 'Rebuilding...' : 'Rebuild summaries'}
            </Button>
            <Button variant="outline" size="sm" onClick={loadHistory} data-testid="refresh-status">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(dataStatus).map(([key, count]) => {
            const labels = { sales: 'Sales', order_fulfilment: 'Order Fulfilment', shops: 'Outlets', skus: 'SKUs', user_list: 'Field Executives', beat_plans: 'Beat Plans', fe_visits: 'FE Visits (TC+PC)' };
            const deps = { sales: 'Schemes', order_fulfilment: 'Overview, Territory, Distribution, Products, Comparison, Alerts, Outlets', shops: 'Distribution, Map, Alerts', skus: 'Products, Distribution', user_list: 'Performance', beat_plans: 'Alerts', fe_visits: 'Visits' };
            return (
            <div key={key} className="p-2.5 rounded-md bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{labels[key] || key}</span>
                <span className={`text-xs font-bold ${count > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                  {count?.toLocaleString('en-IN')}
                </span>
              </div>
              {deps[key] && <p className="text-[10px] text-muted-foreground mt-1">{deps[key]}</p>}
            </div>
          );
          })}
        </div>
      </div>

      {/* Upload */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <Upload size={16} strokeWidth={1.5} /> Upload CSV Data
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">File Type</Label>
            <Select value={fileType || undefined} onValueChange={(v) => { setFileType(v); setReplacePrevious(false); }}>
              <SelectTrigger data-testid="upload-file-type"><SelectValue placeholder="Select file type..." /></SelectTrigger>
              <SelectContent>
                {FILE_TYPES.map(ft => (
                  <SelectItem key={ft.value} value={ft.value}>
                    <div><span className="font-medium">{ft.label}</span><span className="text-muted-foreground text-xs ml-2">{ft.desc}</span></div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">CSV File</Label>
            <Input type="file" ref={fileRef} accept=".csv,.gz,.zip" data-testid="upload-file-input" />
          </div>
          {(fileType === 'beat_plans' || fileType === 'outlets') && (
            <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid={`replace-${fileType === 'outlets' ? 'outlets' : 'beat-plans'}-option`}>
              <Checkbox id="replace-previous" checked={replacePrevious} onCheckedChange={(v) => setReplacePrevious(v === true)} className="mt-0.5" data-testid={`replace-${fileType === 'outlets' ? 'outlets' : 'beat-plans'}-checkbox`} />
              <div className="space-y-0.5">
                <label htmlFor="replace-previous" className="text-sm font-medium cursor-pointer select-none">{fileType === 'outlets' ? 'Replace the outlet master' : 'Replace existing beat plans'}</label>
                <p className="text-xs text-muted-foreground">{fileType === 'outlets'
                  ? 'Outlets missing from this file are marked inactive, so they drop out of beats and briefings. Use only with the full outlet master export.'
                  : 'Deletes ALL previously imported beat plan records before importing this file. Use when uploading a fresh full plan to avoid duplicates from older imports.'}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button onClick={() => handleUpload(false)} disabled={uploading || dupChecking || !fileType} data-testid="upload-submit-btn">
              {dupChecking ? <Loader2 size={14} className="mr-2 animate-spin" /> : uploading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
              {dupChecking ? 'Checking...' : uploading ? (uploadPhase === 'compressing' ? 'Compressing...' : 'Uploading...') : 'Upload'}
            </Button>
            {/* <Button variant="outline" onClick={handleSeed} disabled={seeding} data-testid="seed-data-btn">
              {seeding ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Database size={14} className="mr-2" />}
              {seeding ? 'Seeding...' : 'Seed Sample Data'}
            </Button> */}
          </div>
          {/* Upload Progress */}
          {uploadPhase && (
            <div className="space-y-2 animate-fade-in-up" data-testid="upload-progress-section">
              <div className="flex items-center justify-between text-xs">
                <span className="font-heading font-medium text-foreground">
                  {uploadPhase === 'compressing' && 'Compressing file...'}
                  {uploadPhase === 'uploading' && 'Uploading file...'}
                  {uploadPhase === 'processing' && 'Processing & deduplicating records...'}
                  {uploadPhase === 'done' && 'Complete!'}
                </span>
                <span className="text-muted-foreground">
                  {uploadPhase === 'done' ? '100%' : `${uploadProgress}%`}
                  {uploadEta && uploadPhase !== 'done' && ` \u00B7 ${uploadEta} remaining`}
                </span>
              </div>
              <Progress value={uploadProgress} className="h-2" data-testid="upload-progress-bar" />
              {uploadPhase === 'processing' && (
                <p className="text-[11px] text-muted-foreground">Checking for duplicates and upserting records. Larger files take longer.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data Coverage Dashboard */}
      <div className="bg-card border border-border rounded-md p-5" data-testid="data-coverage-panel">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <Database size={16} strokeWidth={1.5} /> Data Coverage
        </h3>
        {!coverage ? (
          <p className="text-xs text-muted-foreground">Loading coverage data...</p>
        ) : (
        <div className="space-y-4">
            {/* Order Fulfilment coverage */}
            {coverage.order_fulfilment?.length > 0 && (
              <CoverageBar data={coverage.order_fulfilment} label="Order Fulfilment" color="bg-primary" />
            )}
            {/* Sales coverage */}
            {coverage.sales?.length > 0 && (
              <CoverageBar data={coverage.sales} label="Sales (Primary)" color="bg-primary/70" />
            )}
            {/* TC visit coverage */}
            {coverage.fe_visits_tc?.length > 0 && (
              <CoverageBar data={coverage.fe_visits_tc} label="FE Visits (TC)" color="bg-green-500" />
            )}
            {/* Static data summary */}
            <div className="flex flex-wrap gap-4 pt-2 border-t border-border text-xs text-muted-foreground">
              <span>Outlets: <strong className="text-foreground">{coverage.static?.shops?.toLocaleString('en-IN')}</strong></span>
              <span>SKUs: <strong className="text-foreground">{coverage.static?.skus?.toLocaleString('en-IN')}</strong></span>
              <span>FEs: <strong className="text-foreground">{coverage.static?.users?.toLocaleString('en-IN')}</strong></span>
              <span>Beats: <strong className="text-foreground">{coverage.static?.beats?.toLocaleString('en-IN')}</strong></span>
              {coverage.static?.targets > 0 && <span>Targets: <strong className="text-foreground">{coverage.static.targets}</strong></span>}
            </div>
          </div>
        )}
      </div>

      {/* Upload History */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <FileText size={16} strokeWidth={1.5} /> Upload History
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">File</TableHead>
              <TableHead className="text-xs">Data Period</TableHead>
              <TableHead className="text-right text-xs">Records</TableHead>
              <TableHead className="text-xs">Uploaded By</TableHead>
              <TableHead className="text-xs">Upload Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((h, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm capitalize">{h.file_type?.replace('_', ' ')}</TableCell>
                <TableCell className="text-sm truncate max-w-[200px]">{h.file_name || '-'}</TableCell>
                <TableCell className="text-sm">{h.data_from && h.data_to ? <span className="text-xs font-medium text-primary">{h.data_from} to {h.data_to}</span> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                <TableCell className="text-right text-sm">{h.records_count?.toLocaleString('en-IN')}</TableCell>
                <TableCell className="text-sm">{h.uploaded_by || '-'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDisplayDate(h.uploaded_at)}</TableCell>
              </TableRow>
            ))}
            {history.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No uploads yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Settings below are the Super Admin's: the keys, the SMTP account, what the briefings
          recommend, and the reset. An Admin uploads and reads the panels above. */}
      {isSuper && <BriefingSettings api={api} />}

      {isSuper && (<>

      {/* Claude AI Key */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2">
          <CheckCircle2 size={16} strokeWidth={1.5} /> Claude AI (Anthropic) API Key
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Required for AI Insights. Get your key from <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">console.anthropic.com</a> → API Keys.</p>
        {aiKeyInfo && (
          <div className="mb-3 text-xs">
            <span className={aiKeyInfo.configured ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}>
              {aiKeyInfo.configured ? `Configured (${aiKeyInfo.source === 'app' ? 'via app' : 'via .env'})` : 'Not configured'}
            </span>
            {aiKeyInfo.key_masked && <span className="text-muted-foreground ml-2 font-mono">{aiKeyInfo.key_masked}</span>}
          </div>
        )}
        <div className="flex gap-3 items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">API Key</Label>
            <SecretInput name="claude-api-key" placeholder="sk-ant-api03-..." value={aiKey} onChange={e => setAiKey(e.target.value)} data-testid="ai-key-input" />
          </div>
          <Button size="sm" onClick={async () => {
            if (!aiKey || !aiKey.startsWith('sk-ant-')) { toast.error("Key must start with 'sk-ant-'"); return; }
            setAiKeySaving(true);
            try { await api.put('/config/ai-key', { key: aiKey }); toast.success('API key saved'); setAiKey(''); loadAiKey(); }
            catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
            finally { setAiKeySaving(false); }
          }} disabled={aiKeySaving || !aiKey} data-testid="ai-key-save-btn">
            {aiKeySaving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
            Save Key
          </Button>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
          <Mail size={16} strokeWidth={1.5} /> Email (SMTP) Settings
        </h3>
        <p className="text-xs text-muted-foreground mb-4">Configure SMTP to enable password reset emails and notifications.</p>
        {smtpLoading ? <p className="text-sm text-muted-foreground">Loading...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">SMTP Host</Label>
                <Input placeholder="smtp.gmail.com" value={smtp.host} onChange={e => setSmtp({...smtp, host: e.target.value})} data-testid="smtp-host" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Port</Label>
                <Input type="number" placeholder="587" value={smtp.port} onChange={e => setSmtp({...smtp, port: parseInt(e.target.value) || 587})} data-testid="smtp-port" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">From Email</Label>
                <Input type="email" placeholder="noreply@company.com" value={smtp.from_email} onChange={e => setSmtp({...smtp, from_email: e.target.value})} data-testid="smtp-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Username</Label>
                <Input placeholder="SMTP username" value={smtp.username} onChange={e => setSmtp({...smtp, username: e.target.value})} data-testid="smtp-username" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Password</Label>
                <SecretInput name="smtp-password" placeholder={smtpHasPassword ? 'Saved — leave blank to keep' : 'SMTP password or app key'} value={smtp.password} onChange={e => setSmtp({...smtp, password: e.target.value})} data-testid="smtp-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Encryption</Label>
                <Select value={smtp.security} onValueChange={v => setSmtp({
                  ...smtp, security: v,
                  // move the port along with the choice, unless it was set to something custom
                  port: [465, 587, 25].includes(smtp.port) ? SECURITY_PORT[v] : smtp.port,
                })}>
                  <SelectTrigger data-testid="smtp-tls"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tls">SSL/TLS (port 465)</SelectItem>
                    <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
                    <SelectItem value="none">None &mdash; mail server on this machine (port 25)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {smtp.security === 'none' && !!smtp.host.trim() && !isLocalHost(smtp.host) && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="smtp-plain-warning">
                <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Without encryption the username and password cross the network in the clear.
                  Use this only for a mail server running on this machine &mdash; localhost or 127.0.0.1.
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button size="sm" onClick={async () => {
                const needsLogin = smtp.security !== 'none';
                if (!smtp.host || !smtp.from_email || (needsLogin && (!smtp.username || (!smtp.password && !smtpHasPassword)))) {
                  toast.error(needsLogin ? 'All SMTP fields required' : 'Host and from address required'); return;
                }
                setSmtpSaving(true);
                try {
                  // a blank password keeps the saved one: the server swaps this placeholder for it
                  await api.put('/config/smtp', { ...smtp, password: smtp.password || '********' });
                  setSmtp(s => ({ ...s, password: '' }));
                  setSmtpHasPassword(true);
                  toast.success('SMTP settings saved');
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
                finally { setSmtpSaving(false); }
              }} disabled={smtpSaving} data-testid="smtp-save-btn">
                {smtpSaving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                Save Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setTestEmailTo(user?.email || ''); setTestEmailOpen(true); }} data-testid="smtp-test-btn">
                <Send size={14} className="mr-1.5" />
                Send Test Email
              </Button>
            </div>
          </div>
        )}
      </div>

      </>)}

      {/* Reset All Data */}
      {isSuper && (
        <div className="bg-card border border-red-200 dark:border-red-900 rounded-md p-5">
          <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2 text-red-600 dark:text-red-400">
            <ShieldAlert size={16} strokeWidth={1.5} /> Danger Zone
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Reset all data — CSV uploads, analytics, alerts, summaries, and AI insights. This action is irreversible and requires email OTP verification.</p>
          <Button variant="outline" size="sm" onClick={() => { setResetStep(1); setResetOtp(''); setResetError(''); }}
            className="border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
            data-testid="reset-all-data-btn">
            <Trash2 size={14} className="mr-1.5" /> Reset All Data
          </Button>
        </div>
      )}

      {/* Rebuild summaries confirmation */}
      <Dialog open={rebuildAsk} onOpenChange={v => { if (!v) setRebuildAsk(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="rebuild-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading pr-8">Rebuild the summary tables?</DialogTitle>
            <DialogDescription>
              The analytics tables are emptied and rebuilt from the uploads. Nothing you have
              uploaded is lost, but the app's figures are unavailable for a minute or two while
              it runs.
            </DialogDescription>
          </DialogHeader>
          {(() => { const h = new Date().getHours(); return h >= 18 && h < 21; })() && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" data-testid="rebuild-evening-warning">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                It is the evening. The scorecards and the leaders' summaries are built and sent in
                this window, and they will wait behind this rebuild. Unless something is visibly
                wrong, leave it until the sends are done.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => setRebuildAsk(false)}>Cancel</Button>
            <Button size="sm" onClick={doRebuild} disabled={rebuilding} data-testid="rebuild-confirm-btn">
              {rebuilding ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Rebuild
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetStep > 0} onOpenChange={v => { if (!v) { setResetStep(0); setResetOtp(''); setResetError(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-data-dialog" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600 dark:text-red-400 flex items-center gap-2 pr-8">
              <AlertTriangle size={18} /> Reset All Data
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 && 'This will permanently delete ALL data — raw uploads, generated alerts, analytics summaries, and AI insights. Dashboards will show empty state.'}
              {resetStep === 2 && `A verification OTP has been sent to ${user?.email}. Enter it below to proceed.`}
            </DialogDescription>
          </DialogHeader>

          {resetError && <p className="text-sm text-destructive font-medium" data-testid="reset-error">{resetError}</p>}

          {/* Step 1: Confirm intent */}
          {resetStep === 1 && (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-md p-3">
                <p className="text-xs text-red-700 dark:text-red-300 font-medium">The following will be deleted:</p>
                <ul className="text-xs text-red-600 dark:text-red-400 mt-1 space-y-0.5 list-disc list-inside">
                  <li>Raw data: Sales, Orders, Outlets, SKUs, Field Executives, Beat Plans, FE Visits (TC/PC), FE Targets</li>
                  <li>Generated alerts and alert batches</li>
                  <li>All analytics summaries (Overview, Performance, Products, Visits, etc.)</li>
                  <li>Upload history and AI Insights</li>
                </ul>
                <p className="text-xs text-red-700 dark:text-red-300 font-medium mt-2">NOT affected: User accounts, Audit log, Alert rules, SMTP settings, API keys</p>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setResetStep(0)}>Cancel</Button>
                <Button size="sm" onClick={async () => {
                  setResetLoading(true); setResetError('');
                  try {
                    await api.post('/config/reset/request-otp');
                    setResetStep(2);
                  } catch (err) { setResetError(err.response?.data?.detail || 'Failed to send OTP'); }
                  finally { setResetLoading(false); }
                }} disabled={resetLoading} className="bg-red-600 text-white hover:bg-red-700" data-testid="reset-send-otp-btn">
                  {resetLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Mail size={14} className="mr-1.5" />}
                  Send Verification OTP
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Enter OTP */}
          {resetStep === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">6-Digit OTP</Label>
                <Input type="text" placeholder="123456" value={resetOtp}
                  onChange={e => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6} className="text-center text-xl tracking-[0.5em] font-mono font-bold"
                  data-testid="reset-otp-input" />
                <p className="text-[11px] text-muted-foreground">Check your email for the verification code. Expires in 10 minutes.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setResetStep(0)}>Cancel</Button>
                <Button size="sm" onClick={async () => {
                  if (resetOtp.length !== 6) { setResetError('Enter a 6-digit OTP'); return; }
                  setResetLoading(true); setResetError('');
                  try {
                    const res = await api.post('/config/reset/confirm', { otp: resetOtp });
                    toast.success(res.data.message);
                    setResetStep(0);
                    loadHistory();
                  } catch (err) { setResetError(err.response?.data?.detail || 'Verification failed'); }
                  finally { setResetLoading(false); }
                }} disabled={resetLoading || resetOtp.length !== 6} className="bg-red-600 text-white hover:bg-red-700" data-testid="reset-confirm-btn">
                  {resetLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
                  Confirm Reset
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Email Modal */}
      <Dialog open={testEmailOpen} onOpenChange={v => { if (!v) setTestEmailOpen(false); }}>
        <DialogContent className="sm:max-w-sm" data-testid="test-email-dialog" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Send Test Email</DialogTitle>
            <DialogDescription>Verify SMTP configuration by sending a test email</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Recipient Email</Label>
            <Input type="email" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)} placeholder="recipient@company.com" data-testid="test-email-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setTestEmailOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={async () => {
              if (!testEmailTo) { toast.error('Enter an email address'); return; }
              setSmtpTesting(true);
              try {
                const res = await api.post(`/config/smtp/test?to_email=${encodeURIComponent(testEmailTo)}`);
                toast.success(res.data.message);
                setTestEmailOpen(false);
              } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
              finally { setSmtpTesting(false); }
            }} disabled={smtpTesting || !testEmailTo} data-testid="test-email-send-btn">
              {smtpTesting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate File Warning Modal */}
      <Dialog open={!!dupInfo} onOpenChange={v => { if (!v) setDupInfo(null); }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={e => e.preventDefault()} data-testid="duplicate-warning-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-600 dark:text-amber-400 flex items-center gap-2">
              <Upload size={18} /> File Already Uploaded
            </DialogTitle>
            <DialogDescription>
              {dupInfo?.match_type === 'exact' ? 'This exact file was already uploaded.' : 'A file with the same name was uploaded recently.'}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3 space-y-1 text-sm">
            <p><strong>File:</strong> {dupInfo?.file_name}</p>
            <p><strong>Type:</strong> {dupInfo?.file_type?.replace('_', ' ')}</p>
            <p><strong>Records:</strong> {dupInfo?.records_count?.toLocaleString('en-IN')}</p>
            <p><strong>Uploaded by:</strong> {dupInfo?.uploaded_by}</p>
            <p><strong>Date:</strong> {formatDisplayDateTime(dupInfo?.uploaded_at)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDupInfo(null)}>Cancel</Button>
            <Button size="sm" onClick={() => { setDupInfo(null); handleUpload(true); }} data-testid="upload-anyway-btn">
              <Upload size={14} className="mr-1.5" /> Upload Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
